import type { NextPage } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import {
  ArrowDownBoldIcon,
  ArrowLeft2Icon,
  ArrowRight2Icon,
  CloseCircleIcon,
  SignPostBoldIcon,
} from "../../../app/component/commons/icon/SvgIcon";
import Image from "../../../app/component/commons/image/Image";
import {
  LoadingFacility,
  NotFound,
} from "../../../app/component/commons/loading/Skeleton";
import Meta from "../../../app/component/commons/meta/Meta";
import SelectSearch from "../../../app/component/commons/select/SelectSearch";
import DefaultLayout from "../../../app/layouts/DefaultLayout";
import { client } from "../../../app/utils/axios";

const Index: NextPage = () => {
  const router = useRouter();
  const [mode, setMode] = useState("");
  const [items, setItems] = useState<{
    status: "LOADING" | "NOTFOUND" | "SUCCESS";
    data: {
      id: number;
      name: string;
      slug: string;
      city: {
        id: number;
        name: string;
        slug: string;
        state: {
          id: number;
          name: string;
          slug: string;
        };
      };
      address: string;
      zipcode: string;
      lat: number;
      lng: number;
      image: {
        lg: string;
        md: string;
        sm: string;
        xs: string;
      };
    }[];
  }>({
    status: "LOADING",
    data: [],
  });

  const [params, setParams] = useState<{
    page: number | null;
    keyword: string;
    filter_state: number | null;
    filter_city: number | null;
  }>({
    page: null,
    keyword: "",
    filter_state: null,
    filter_city: null,
  });

  const [region, setRegion] = useState<{
    state: {
      id: number | null;
      name: string;
    };
    city: {
      id: number | null;
      name: string;
    };
  }>({
    state: {
      id: null,
      name: "",
    },
    city: {
      id: null,
      name: "",
    },
  });

  const [paging, setPaging] = useState<{
    current_page: number;
    from: number;
    last_page: number;
    to: number;
    total: number;
  }>({
    current_page: 1,
    from: 1,
    last_page: 1,
    to: 20,
    total: 20,
  });

  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
  const [states, setStates] = useState<{ id: number; name: string }[]>([]);
  const [trigger, setTrigger] = useState(false);

  const getData = async () => {
    setItems({ ...items, status: "LOADING" });
    try {
      const { data } = await client.get("/m/facility", { params });
      if (data) {
        setItems({
          status: "SUCCESS",
          data: data.items,
        });
        setPaging({
          current_page: data.current_page,
          from: data.from,
          last_page: data.last_page,
          to: data.to,
          total: data.total,
        });
      }
    } catch (error) {
      setItems({
        status: "NOTFOUND",
        data: [],
      });
    }
  };

  const getCities = async () => {
    try {
      const { data } = await client.get("/masterdata/cities?all=1", {
        params: {
          state_id: region.state.id,
        },
      });
      if (data) {
        setCities(data);
      }
    } catch (error) {
      setCities([]);
    }
  };

  const getStates = async () => {
    try {
      const { data } = await client.get("/masterdata/states");
      if (data) {
        setStates(data);
      }
    } catch (error) {
      setStates([]);
    }
  };

  useEffect(() => {
    // window.analytics.page("viewed");
    if (window.innerWidth < 768) {
      setMode("mobile");
    } else {
      setMode("desktop");
    }
    getStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (region.state.id) {
      getCities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  useEffect(() => {
    const { state, city, filter_state, filter_city } = router.query as any;

    if (state && city) {
      setRegion({
        city: { id: Number(filter_city), name: city },
        state: { id: Number(filter_state), name: state },
      });
      setParams({
        ...params,
        filter_state: Number(filter_state),
        filter_city: Number(filter_city),
      });
      setTrigger(true);
    } else if (state) {
      setRegion({
        city: { id: null, name: "" },
        state: { id: Number(filter_state), name: state },
      });
      setParams({
        ...params,
        filter_state: Number(filter_state),
        filter_city: null,
      });
      setTrigger(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  useEffect(() => {
    if (trigger) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  return (
    <>
      <Meta
        title="Storage Facility"
        description="Here's our storage facilities around united states"
      />
      <DefaultLayout topFooter="!mt-20">
        {/* BANNER */}
        <div className="lg:container lg:mx-auto">
          <div className="gradient-main min-h-full lg:w-[68.125rem] p-6 lg:rounded-t-[3rem] mx-auto mt-7 md:mt-[4.25rem] relative flex flex-col items-center">
            <div className="absolute lg:right-[-2rem] lg:top-[-1.5rem] top-[-2.5rem] right-[1rem]">
              <Image
                src="/images/box.svg"
                alt="Box"
                className="lg:w-[7.75rem] lg:h-[7.75rem] w-[60px] h-[60px]"
              />
            </div>
            <div className="absolute lg:left-[-3.5rem] lg:top-[5rem] left-[0px] top-[-1rem]">
              <Image
                src="/images/bid.svg"
                alt="Box"
                className="lg:w-[7.75rem] lg:h-[7.75rem] w-[60px] h-[60px]"
              />
            </div>
            <div className="uppercase font-medium md:font-bold text-warning-500 text-regular-tight md:text-large-none">
              search for facilities
            </div>
            <div className="text-neutral-900 font-bold text-title3 leading-[2.125rem] md:!text-title1 mt-6 md:mt-3.5">
              Storage Facilities
            </div>

            {/* FILTER */}
            <div className="mt-7">
              <div className="flex items-center bg-white rounded-full px-3.5 py-2 border border-neutral-300">
                <label
                  htmlFor="search-dropdown"
                  className="mb-2 text-sm font-medium text-gray-900 sr-only "
                >
                  Search
                </label>

                <div className="relative md:w-[27.188rem]">
                  <input
                    type="text"
                    id="search-dropdown"
                    className="block p-2.5 w-full z-20 text-regular-normal text-neutral-900 focus:outline-none placeholder:text-neutral-500 border-r border-neutral-300"
                    placeholder="Search..."
                    required
                    value={params.keyword}
                    onChange={(e) => {
                      e.preventDefault();

                      setParams({ ...params, keyword: e.target.value });
                    }}
                  />
                </div>
                <div>
                  <SelectSearch
                    options={states}
                    onChangeValue={(e) => {
                      setRegion({ city: { id: null, name: "" }, state: e });
                      setParams({
                        ...params,
                        filter_state: e.id,
                        filter_city: null,
                      });
                    }}
                    value={{
                      id: region.state.id,
                      name: region.state.name,
                    }}
                    buttonElement={
                      <>
                        <div className="flex-shrink-0 z-10 inline-flex items-center pl-7 text-small-normal font-medium text-center text-neutral-900 focus:outline-none">
                          <div className="flex flex-row items-center space-x-3">
                            <div className="">
                              {region.state.name || "State"}
                            </div>
                            <div
                              className="text-neutral-900"
                              onClick={() => {
                                if (params.filter_state) {
                                  setRegion({
                                    state: { id: null, name: "" },
                                    city: { id: null, name: "" },
                                  });
                                  setParams({
                                    ...params,
                                    filter_state: null,
                                    filter_city: null,
                                  });
                                }
                              }}
                            >
                              {params.filter_state ? (
                                <CloseCircleIcon size="14" />
                              ) : (
                                <ArrowDownBoldIcon />
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    }
                  />
                </div>
                <div className="md:block hidden">
                  <SelectSearch
                    options={cities}
                    onChangeValue={(e) => {
                      setRegion({ ...region, city: e });
                      setParams({ ...params, filter_city: e.id });
                    }}
                    value={{
                      id: region.city.id,
                      name: region.city.name,
                    }}
                    buttonElement={
                      <div className="flex-shrink-0 z-10 inline-flex items-center pl-7 text-small-normal font-medium text-center text-neutral-900 focus:outline-none">
                        <div className="flex flex-row items-center space-x-3">
                          <div className="">{region.city.name || "City"}</div>
                          <div
                            className="text-neutral-900"
                            onClick={() => {
                              if (params.filter_city) {
                                setRegion({
                                  ...region,
                                  city: { id: null, name: "" },
                                });
                                setParams({ ...params, filter_city: null });
                              }
                            }}
                          >
                            {params.filter_city ? (
                              <CloseCircleIcon size="14" />
                            ) : (
                              <ArrowDownBoldIcon />
                            )}
                          </div>
                        </div>
                      </div>
                    }
                    textClassName="text-small-normal"
                    disabled={!params.filter_state}
                  />
                </div>
              </div>
            </div>

            {/* DATATABLE */}
            <div className="mt-[2.125rem] w-full lg:w-[54.25rem]">
              <div className="overflow-x-auto md:min-h-[40rem]">
                <table className="w-full text-sm text-left text-gray-500 ">
                  <thead className="text-small-normal text-primary-900 capitalize ">
                    <tr>
                      <th
                        scope="col"
                        className="p-3 font-medium min-w-[21.875rem]"
                      >
                        Description
                      </th>
                      <th
                        scope="col"
                        className="p-3 font-medium min-w-[15.625rem]"
                      >
                        Address
                      </th>
                      <th scope="col" className="p-3 font-medium">
                        City
                      </th>
                      <th scope="col" className="p-3 font-medium">
                        State
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={`${
                      items.data.length > 0 ? "bg-white" : "bg-transparent"
                    } p-2.5 rounded-xl border border-neutral-100`}
                  >
                    {items.status === "SUCCESS" ? (
                      items.data.length > 0 ? (
                        items.data.map((e, key: number) => (
                          <tr key={key} className="border-b border-neutral-100">
                            <td scope="row" className="p-3">
                              <Link href={`/facility/detail/${e.slug}`}>
                                <a className="flex flex-row items-center space-x-2">
                                  <div className="min-w-[2.5rem]">
                                    <Image
                                      src={e.image.md}
                                      alt="table"
                                      className="w-10 h-10 object-cover rounded-lg"
                                    />
                                  </div>
                                  <div className="flex flex-col space-y-1.5">
                                    <div className="font-bold text-neutral-900 text-small-tight break-all line-clamp-1">
                                      {e.name}
                                    </div>
                                    <div className="text-tiny-tight font-medium text-primary-500">
                                      {`#${e.id}`}
                                    </div>
                                  </div>
                                </a>
                              </Link>
                            </td>
                            <td className="p-3 ">
                              <div className="text-tiny-tight text-neutral-600 break-all line-clamp-1">
                                {e.address}
                              </div>
                              <div className="mt-1 w-fit py-1 px-2 rounded-lg bg-info-500 flex flex-row items-center space-x-1 text-white">
                                <div>
                                  <SignPostBoldIcon size="14" />
                                </div>
                                <div className="text-tiny-tight">
                                  {e.zipcode}
                                </div>
                              </div>
                            </td>
                            <td className="p-3 text-small-normal font-medium text-neutral-900 ">
                              <Link href={`/facility/city/${e.city.slug}`}>
                                {e.city.name}
                              </Link>
                            </td>
                            <td className="p-3 text-small-normal font-medium text-neutral-900 ">
                              <Link
                                href={`/facility/state/${e.city.state.slug}`}
                              >
                                {e.city.state.name}
                              </Link>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="p-3">
                            <NotFound />
                          </td>
                        </tr>
                      )
                    ) : items.status === "LOADING" ? (
                      <>
                        {Array.from({
                          length:
                            items.data.length > 0 ? items.data.length : 20,
                        }).map((e, key: number) => (
                          <LoadingFacility key={key} />
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td colSpan={4} className="p-3">
                          <NotFound />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {items.data.length > 0 && (
                <div className="flex flex-row items-center justify-center space-x-6 mt-6">
                  <div
                    className={`flex items-center bg-white border border-neutral-200 rounded-full p-2 ${
                      paging.current_page <= 1
                        ? "text-neutral-500"
                        : "text-primary-500 cursor-pointer"
                    }`}
                    onClick={() => {
                      if (paging.current_page > 1) {
                        setParams({
                          ...params,
                          page:
                            paging.current_page - 1 <= 1
                              ? 1
                              : paging.current_page - 1,
                        });
                      }
                    }}
                  >
                    <ArrowLeft2Icon />
                  </div>

                  {/* PAGINATION NEXT */}
                  <div
                    className={`flex items-center bg-white border border-neutral-200 rounded-full p-2 ${
                      paging.current_page === paging.last_page
                        ? "text-neutral-500"
                        : "text-primary-500 cursor-pointer"
                    }`}
                    onClick={() => {
                      if (paging.current_page !== paging.last_page) {
                        setParams({
                          ...params,
                          page:
                            paging.current_page + 1 >= paging.last_page
                              ? paging.last_page
                              : paging.current_page + 1,
                        });
                      }
                    }}
                  >
                    <ArrowRight2Icon />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DefaultLayout>
    </>
  );
};

export default Index;
